import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const HealthcareLifeSciences = () => {
  return (
    <>
      <SEO 
        title="Healthcare & Life Sciences AI Solutions"
        description="Transform healthcare delivery with AI-powered diagnostics, patient care systems, clinical research automation, and regulatory compliance solutions. Enterprise healthcare technology by Abacies Logiciels."
        keywords="healthcare AI, medical AI solutions, clinical research automation, healthcare analytics, patient care systems, medical diagnostics AI, life sciences technology, healthcare digital transformation, HIPAA compliant software, medical data analytics"
      />

      {/* Hero Section */}
      <section className="hero-section py-10">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-blush-light/30 rounded-full text-primary-red font-semibold mb-4 animate-fade-in">
              Healthcare & Life Sciences
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-slide-up">
              Transforming Healthcare Through <span className="text-gradient">Intelligent AI Solutions</span>
            </h1>
            <p className="text-xl text-titanium leading-relaxed mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Empower healthcare providers, researchers, and life sciences organizations with cutting-edge AI technology that enhances patient outcomes, accelerates research, and optimizes clinical operations while maintaining the highest standards of data security and regulatory compliance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/contact" className="btn-primary">
                Schedule Consultation
              </Link>
              <Link to="/ai-hub" className="btn-secondary">
                Explore AI Capabilities
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Challenges */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Healthcare Challenges We Solve</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Addressing critical pain points in modern healthcare delivery and life sciences research
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Diagnostic Accuracy',
                description: 'Enhance diagnostic precision with AI-powered image analysis and pattern recognition systems',
                icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
              },
              {
                title: 'Patient Care Quality',
                description: 'Improve patient outcomes through predictive analytics and personalized treatment recommendations',
                icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z'
              },
              {
                title: 'Research Acceleration',
                description: 'Speed up drug discovery and clinical trials with automated data analysis and insights',
                icon: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z'
              },
              {
                title: 'Operational Efficiency',
                description: 'Streamline hospital operations with intelligent resource allocation and workflow automation',
                icon: 'M13 10V3L4 14h7v7l9-11h-7z'
              },
              {
                title: 'Regulatory Compliance',
                description: 'Maintain HIPAA, FDA, and international regulatory compliance with automated monitoring',
                icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'
              },
              {
                title: 'Data Integration',
                description: 'Unify disparate health information systems for comprehensive patient insights',
                icon: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4'
              }
            ].map((challenge, index) => (
              <div key={index} className="card group hover:border-primary-red/30 transition-all duration-300">
                <div className="w-14 h-14 bg-blush-light/40 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blush-light/60 transition-all duration-300">
                  <svg className="w-7 h-7 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={challenge.icon} />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-charcoal">{challenge.title}</h3>
                <p className="text-titanium">{challenge.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core AI Capabilities */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">AI-Powered Healthcare Solutions</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Comprehensive AI orchestration designed specifically for healthcare and life sciences applications
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            {[
              {
                title: 'Clinical Decision Support',
                description: 'Intelligent routing engine that analyzes patient data across multiple sources to provide evidence-based treatment recommendations and flag potential risks in real-time.',
                features: ['Multi-source data integration', 'Real-time risk assessment', 'Evidence-based protocols', 'Alert prioritization']
              },
              {
                title: 'Medical NLP & Documentation',
                description: 'Advanced natural language processing for clinical documentation, medical coding, and extracting insights from unstructured medical records and research papers.',
                features: ['Clinical note generation', 'Automated ICD-10 coding', 'Medical entity extraction', 'Semantic search']
              },
              {
                title: 'Predictive Patient Analytics',
                description: 'Temporal analysis engine that forecasts patient outcomes, readmission risks, and disease progression patterns to enable proactive intervention strategies.',
                features: ['Readmission prediction', 'Disease progression modeling', 'Population health analytics', 'Resource forecasting']
              },
              {
                title: 'Diagnostic Image Analysis',
                description: 'Computer vision AI for radiology, pathology, and medical imaging analysis with precision comparable to specialist physicians and continuous learning capabilities.',
                features: ['Radiology image analysis', 'Pathology slide review', 'Anomaly detection', 'Multi-modal imaging']
              },
              {
                title: 'Treatment Protocol Optimization',
                description: 'Rules and decision engines that optimize treatment protocols based on patient characteristics, latest research, and institutional best practices.',
                features: ['Personalized protocols', 'Drug interaction checking', 'Dosage optimization', 'Outcome tracking']
              },
              {
                title: 'Research Data Intelligence',
                description: 'AI-powered analysis of clinical trial data, genomic sequences, and research literature to accelerate drug discovery and identify new treatment pathways.',
                features: ['Literature mining', 'Genomic analysis', 'Trial optimization', 'Biomarker discovery']
              }
            ].map((solution, index) => (
              <div key={index} className="card hover:shadow-xl transition-all duration-300">
                <h3 className="text-2xl font-bold mb-4 text-charcoal">{solution.title}</h3>
                <p className="text-titanium mb-6">{solution.description}</p>
                <div className="space-y-2">
                  {solution.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-sm">
                      <svg className="w-4 h-4 text-primary-red mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-graphite">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Real-World Healthcare Applications</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Proven implementations across diverse healthcare settings
            </p>
          </div>
          <div className="space-y-8">
            {[
              {
                title: 'Hospital Systems',
                icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
                applications: [
                  'Emergency department triage optimization',
                  'ICU patient monitoring and early warning systems',
                  'Operating room scheduling and resource allocation',
                  'Inpatient flow management and bed utilization'
                ]
              },
              {
                title: 'Diagnostic Centers',
                icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
                applications: [
                  'Radiology workflow automation and image analysis',
                  'Pathology slide digitization and AI diagnosis',
                  'Laboratory result interpretation and flagging',
                  'Multi-modal diagnostic data correlation'
                ]
              },
              {
                title: 'Pharmaceutical Research',
                icon: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z',
                applications: [
                  'Drug discovery and molecule screening',
                  'Clinical trial patient matching and recruitment',
                  'Adverse event detection and reporting',
                  'Regulatory submission documentation automation'
                ]
              },
              {
                title: 'Outpatient Clinics',
                icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
                applications: [
                  'Appointment scheduling optimization',
                  'Chronic disease management programs',
                  'Patient engagement and follow-up automation',
                  'Telemedicine platform integration'
                ]
              }
            ].map((useCase, index) => (
              <div key={index} className="card hover:border-primary-red/30 transition-all duration-300">
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  <div className="w-16 h-16 bg-blush-light/40 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-8 h-8 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={useCase.icon} />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-4 text-charcoal">{useCase.title}</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {useCase.applications.map((app, idx) => (
                        <div key={idx} className="flex items-start">
                          <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                          <span className="text-titanium">{app}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Healthcare-Grade Technology Stack</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Enterprise-level infrastructure built for healthcare's unique requirements
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                category: 'Compliance',
                items: ['HIPAA Compliant', 'FDA 21 CFR Part 11', 'GDPR Ready', 'SOC 2 Type II']
              },
              {
                category: 'Integration',
                items: ['HL7 FHIR', 'DICOM', 'Epic Integration', 'Cerner Millennium']
              },
              {
                category: 'AI/ML',
                items: ['TensorFlow Medical', 'PyTorch Healthcare', 'Custom Models', 'Federated Learning']
              },
              {
                category: 'Security',
                items: ['End-to-End Encryption', 'Access Control', 'Audit Trails', 'PHI Protection']
              }
            ].map((tech, index) => (
              <div key={index} className="card">
                <h3 className="text-lg font-semibold mb-4 text-charcoal">{tech.category}</h3>
                <ul className="space-y-2">
                  {tech.items.map((item, idx) => (
                    <li key={idx} className="flex items-center text-sm text-titanium">
                      <svg className="w-4 h-4 text-primary-red mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results & Impact */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Measurable Healthcare Impact</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Real outcomes from our healthcare AI implementations
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { metric: '45%', label: 'Reduction in Diagnostic Errors', description: 'AI-assisted radiology analysis' },
              { metric: '30%', label: 'Faster Clinical Trials', description: 'Automated patient matching' },
              { metric: '60%', label: 'Improved Care Coordination', description: 'Intelligent routing systems' },
              { metric: '25%', label: 'Lower Readmission Rates', description: 'Predictive patient monitoring' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-primary-red mb-2">{stat.metric}</div>
                <div className="text-lg font-semibold text-charcoal mb-2">{stat.label}</div>
                <p className="text-sm text-titanium">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Highlight */}
      <section className="py-5 md:py-5 bg-gradient-to-br from-alabaster to-pearl-white">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="card bg-white shadow-xl">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-primary-red rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4 flex-1">
                  <div className="text-sm text-primary-red font-semibold mb-2">SUCCESS STORY</div>
                  <h3 className="text-2xl font-bold text-charcoal mb-4">
                    Major Teaching Hospital Transforms Patient Care with AI
                  </h3>
                </div>
              </div>
              <p className="text-titanium mb-6 leading-relaxed">
                A 800-bed academic medical center implemented our comprehensive AI orchestration platform across emergency, critical care, and diagnostic departments. The system integrated with existing EMR infrastructure while adding intelligent decision support, predictive analytics, and automated workflow optimization.
              </p>
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="text-center p-4 bg-blush-light/20 rounded-lg">
                  <div className="text-2xl font-bold text-primary-red mb-1">35%</div>
                  <div className="text-sm text-charcoal">Faster Diagnoses</div>
                </div>
                <div className="text-center p-4 bg-blush-light/20 rounded-lg">
                  <div className="text-2xl font-bold text-primary-red mb-1">$12M</div>
                  <div className="text-sm text-charcoal">Annual Savings</div>
                </div>
                <div className="text-center p-4 bg-blush-light/20 rounded-lg">
                  <div className="text-2xl font-bold text-primary-red mb-1">98%</div>
                  <div className="text-sm text-charcoal">Clinician Satisfaction</div>
                </div>
              </div>
              <Link to="/contact" className="text-primary-red font-semibold hover:underline">
                Read Full Case Study →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Our Healthcare Implementation Process</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Structured approach ensuring seamless integration with minimal disruption
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Assessment & Planning',
                description: 'Comprehensive evaluation of current systems, workflows, and compliance requirements'
              },
              {
                step: '02',
                title: 'Custom Development',
                description: 'Tailored AI solutions designed for your specific healthcare environment and use cases'
              },
              {
                step: '03',
                title: 'Integration & Testing',
                description: 'Seamless connection with existing EMR, PACS, and clinical systems with rigorous validation'
              },
              {
                step: '04',
                title: 'Training & Support',
                description: 'Comprehensive staff training and ongoing technical support with continuous optimization'
              }
            ].map((phase, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-primary-red text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {phase.step}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-charcoal">{phase.title}</h3>
                <p className="text-titanium">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Solutions */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Related Healthcare Solutions</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Explore our comprehensive suite of healthcare technology services
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'AI & Machine Learning',
                description: 'Advanced AI models for medical applications',
                link: '/services/ai-machine-learning'
              },
              {
                title: 'Data Analytics',
                description: 'Healthcare data intelligence and insights',
                link: '/services/data-analytics'
              },
              {
                title: 'System Integration',
                description: 'EMR and healthcare system connectivity',
                link: '/services/system-integration'
              }
            ].map((solution, index) => (
              <Link key={index} to={solution.link} className="card hover:shadow-xl transition-all duration-300 block">
                <h3 className="text-xl font-semibold mb-3 text-charcoal">{solution.title}</h3>
                <p className="text-titanium mb-4">{solution.description}</p>
                <span className="text-primary-red font-semibold">
                  Learn More →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 md:py-5 bg-primary-red text-white">
        <div className="page-container text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Transform Healthcare Delivery?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Partner with us to implement cutting-edge AI solutions that improve patient outcomes, accelerate research, and optimize healthcare operations while maintaining the highest standards of security and compliance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
              Schedule Healthcare Consultation
            </Link>
            <Link to="/company/our-story" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
              View Our Healthcare Portfolio
            </Link>
          </div>
          <p className="mt-8 text-sm opacity-80">
            HIPAA Compliant • FDA Ready • SOC 2 Certified • ISO 27001
          </p>
        </div>
      </section>
    </>
  );
};

export default HealthcareLifeSciences;